import type { RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Pagination } from '../../Pagination/Pagination';
import type { TableInstance } from '../tableFeatures';

interface TablePaginationProps<TData extends RowData> {
  paginated?: boolean;
  table: TableInstance<TData>;
}

export function TablePagination<TData extends RowData>({
  paginated,
  table,
}: TablePaginationProps<TData>) {
  const { t } = useTranslation('ui');
  const { pageIndex, pageSize } = table.store.state.pagination;

  if (!paginated || pageSize < 2) return null;

  const paginatedRows = table.getPaginatedRowModel().rows;
  const prePaginatedRows = table.getPrePaginatedRowModel().rows;
  const range =
    prePaginatedRows.length === 0
      ? '0'
      : `${pageSize * pageIndex + 1}-${
          paginatedRows.length - 1 >= pageSize
            ? prePaginatedRows.length
            : pageSize * pageIndex + paginatedRows.length
        }`;

  return (
    <div className="typ-ui mt-4! flex items-center justify-end space-x-4 overflow-x-auto text-neutral-100">
      <span>
        {t('pagination.summary', {
          current: range,
          length: table.getFilteredRowModel().rows.length,
        })}
      </span>
      <Pagination
        pagesCount={table.getPageCount()}
        onNextClick={() => table.nextPage()}
        onPreviousClick={() => table.previousPage()}
        currentPage={pageIndex + 1}
      >
        {Array.from({ length: table.getPageCount() }).map((_, index) => (
          <button onClick={() => table.setPageIndex(index)} key={index}>
            {index + 1}
          </button>
        ))}
      </Pagination>
    </div>
  );
}
